import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Crosshair, Search, MapPin, Building2, Map as MapIcon, Flag, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

// Fix default marker icons for Leaflet (CRA bundling fails to find them)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const NSE_LEVELS = [
  { value: "A/B", label: "A/B — Alto", description: "Ingreso muy alto, profesionistas" },
  { value: "C+", label: "C+ — Medio Alto", description: "Ingreso alto, ejecutivos" },
  { value: "C", label: "C — Medio", description: "Ingreso medio, empleados" },
  { value: "C-", label: "C- — Medio Bajo", description: "Ingreso medio bajo" },
  { value: "D+", label: "D+ — Bajo", description: "Ingreso bajo, obreros calificados" },
  { value: "D/E", label: "D/E — Muy Bajo", description: "Ingreso muy bajo" },
];

// Sub-component: map auto-pans when coords change
const MapPanner = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position && position.lat && position.lng) {
      map.setView([position.lat, position.lng], 16, { animate: true });
    }
  }, [position, map]);
  return null;
};

// Click handler for marker repositioning
const ClickHandler = ({ onClick }) => {
  useMapEvents({
    click: (e) => onClick(e.latlng),
  });
  return null;
};

const LocationStep = ({ location, onChange }) => {
  const [searchInput, setSearchInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);
  const debounceRef = useRef(null);

  const update = (patch) => onChange({ ...location, ...patch });

  // Default: México (centroide aprox.)
  const defaultCenter = [23.6345, -102.5528];
  const center = location.lat && location.lng ? [parseFloat(location.lat), parseFloat(location.lng)] : defaultCenter;
  const hasPin = !!(location.lat && location.lng);

  const reverseGeocode = async (lat, lng) => {
    setReverseLoading(true);
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=es&zoom=16`,
        { headers: { "User-Agent": "Findperty-MVIP/1.0" } }
      );
      const data = await r.json();
      if (data?.address) {
        const a = data.address;
        update({
          lat: parseFloat(lat).toFixed(6),
          lng: parseFloat(lng).toFixed(6),
          colonia: a.suburb || a.neighbourhood || a.quarter || a.residential || a.city_district || location.colonia || "",
          municipio: a.city || a.town || a.village || a.county || a.municipality || location.municipio || "",
          estado: a.state || location.estado || "",
          full_address: data.display_name || location.full_address || "",
        });
      } else {
        update({ lat: parseFloat(lat).toFixed(6), lng: parseFloat(lng).toFixed(6) });
      }
    } catch (e) {
      console.error("reverse geocode failed:", e);
      update({ lat: parseFloat(lat).toFixed(6), lng: parseFloat(lng).toFixed(6) });
    } finally {
      setReverseLoading(false);
    }
  };

  const handleGPS = () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        reverseGeocode(latitude, longitude);
        setGpsLoading(false);
        toast.success("Ubicación detectada");
      },
      (err) => {
        toast.error(err.code === 1 ? "Permiso de ubicación denegado" : "No se pudo obtener tu ubicación");
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSearch = async () => {
    if (!searchInput.trim()) return;
    setSearching(true);
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(searchInput + ", México")}&limit=1&accept-language=es`,
        { headers: { "User-Agent": "Findperty-MVIP/1.0" } }
      );
      const results = await r.json();
      if (results.length > 0) {
        const { lat, lon, display_name } = results[0];
        update({
          lat: parseFloat(lat).toFixed(6),
          lng: parseFloat(lon).toFixed(6),
          full_address: display_name,
        });
        // Then reverse geocode to fill colonia/municipio/estado
        reverseGeocode(parseFloat(lat), parseFloat(lon));
      } else {
        toast.error("Dirección no encontrada");
      }
    } catch (e) {
      toast.error("Error al buscar dirección");
    } finally {
      setSearching(false);
    }
  };

  const handleMapClick = (latlng) => {
    reverseGeocode(latlng.lat, latlng.lng);
  };

  // Debounced manual lat/lng input
  const handleLatLngChange = (field, val) => {
    update({ [field]: val });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const lat = field === "lat" ? parseFloat(val) : parseFloat(location.lat);
      const lng = field === "lng" ? parseFloat(val) : parseFloat(location.lng);
      if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        reverseGeocode(lat, lng);
      }
    }, 800);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="location-step">
      {/* LEFT: controls */}
      <div className="space-y-4">
        <Button
          onClick={handleGPS}
          disabled={gpsLoading}
          className="w-full h-12 bg-primary hover:bg-primary/90 text-slate-900"
          data-testid="gps-button"
        >
          {gpsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Crosshair className="w-4 h-4 mr-2" />}
          Usar mi ubicación actual (GPS)
        </Button>

        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">O buscar por dirección</Label>
          <div className="flex gap-2 mt-1">
            <Input
              placeholder="Av. Plan de Ayala 123, Cuernavaca, Morelos"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleSearch())}
              data-testid="address-search-input"
            />
            <Button onClick={handleSearch} disabled={searching} variant="outline" data-testid="address-search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-1">Geocodificación gratuita vía OpenStreetMap / Nominatim</p>
        </div>

        {/* Auto-populated fields */}
        <Card className="bg-slate-50/50 border-dashed">
          <CardContent className="pt-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              <Badge variant={hasPin ? "default" : "outline"} className="text-xs">
                <MapPin className="w-3 h-3 mr-1" />
                {hasPin ? `${parseFloat(location.lat).toFixed(4)}, ${parseFloat(location.lng).toFixed(4)}` : "Sin coordenadas"}
              </Badge>
              <Badge variant="outline" className="text-xs">
                <Building2 className="w-3 h-3 mr-1" />
                Colonia: {location.colonia || "—"}
              </Badge>
              <Badge variant="outline" className="text-xs">
                <MapIcon className="w-3 h-3 mr-1" />
                Municipio: {location.municipio || "—"}
              </Badge>
              <Badge variant="outline" className="text-xs">
                <Flag className="w-3 h-3 mr-1" />
                Estado: {location.estado || "—"}
              </Badge>
              {reverseLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Latitud</Label>
                <Input
                  type="number"
                  step="any"
                  value={location.lat || ""}
                  onChange={(e) => handleLatLngChange("lat", e.target.value)}
                  placeholder="19.432608"
                  data-testid="lat-input"
                />
              </div>
              <div>
                <Label className="text-xs">Longitud</Label>
                <Input
                  type="number"
                  step="any"
                  value={location.lng || ""}
                  onChange={(e) => handleLatLngChange("lng", e.target.value)}
                  placeholder="-99.133209"
                  data-testid="lng-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div>
          <Label className="text-sm font-medium">Nivel Socioeconómico (escala AMAI)</Label>
          <Select value={location.nse || ""} onValueChange={(v) => update({ nse: v })}>
            <SelectTrigger data-testid="nse-select">
              <SelectValue placeholder="Selecciona NSE" />
            </SelectTrigger>
            <SelectContent>
              {NSE_LEVELS.map((n) => (
                <SelectItem key={n.value} value={n.value}>
                  <div>
                    <div className="font-medium">{n.label}</div>
                    <div className="text-xs text-slate-500">{n.description}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-sm font-medium">Infraestructura relevante cercana</Label>
          <Textarea
            value={location.infraestructura_notes || ""}
            onChange={(e) => update({ infraestructura_notes: e.target.value })}
            placeholder="Hospital ABC a 1 km, Walmart a 500 m, escuela primaria pública en la cuadra, transporte público frecuente, parque..."
            rows={3}
            data-testid="infraestructura-input"
          />
          <p className="text-xs text-slate-400 mt-1">Comercios, escuelas, hospitales, transporte, parques, vías rápidas.</p>
        </div>
      </div>

      {/* RIGHT: map */}
      <div className="space-y-2">
        <Label className="text-sm font-medium flex items-center gap-2">
          <MapIcon className="w-4 h-4" /> Mapa interactivo (haz click para anclar el pin)
        </Label>
        <div className="rounded-lg overflow-hidden border-2 border-slate-200 shadow-sm" style={{ height: "440px" }}>
          <MapContainer
            center={center}
            zoom={hasPin ? 16 : 5}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ClickHandler onClick={handleMapClick} />
            {hasPin && <Marker position={[parseFloat(location.lat), parseFloat(location.lng)]} />}
            {hasPin && <MapPanner position={{ lat: parseFloat(location.lat), lng: parseFloat(location.lng) }} />}
          </MapContainer>
        </div>
        {!hasPin && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 p-2 rounded">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            Usa GPS, búsqueda por dirección o haz click en el mapa para anclar el pin del inmueble.
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationStep;
